import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    TransportAssignmentBulkUpdatePreviewErrorCode,
    TransportAssignmentBulkUpdatePreviewResult,
} from '../transportAssignment.types';
import { TransportOrderBulkUpdateFormStep } from './TransportOrderBulkUpdateFormStep';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import {
    getTransportAssignmentBulkSelection,
    transportAssignmentsSlice,
} from '../../reducers/transportAssignmentsSlice';
import { BulkUpdateValidationFeedback } from '../../sharedComponents/bulkStatusUpdate/BulkUpdateValidationFeedback';
import { BulkStatusError } from '../../sharedComponents/bulkStatusUpdate/BulkStatusErrorMessage';
import { BulkUpdateMaxSizeErrorMessage } from '../../sharedComponents/bulkStatusUpdate/BulkUpdateMaxSizeErrorMessage';
import { MAX_BULK_SELECTION_SIZE } from './TransportOrderBulkUpdate';

type TransportOrderBulkUpdateDialogProps = {
    readonly previewResult: TransportAssignmentBulkUpdatePreviewResult;
    readonly onClose: () => void;
};

enum TransportOrderBulkUpdateDialogStep {
    VALIDATION = 'validation',
    FORM = 'form',
}

export const TransportOrderBulkUpdateDialog = ({ previewResult, onClose }: TransportOrderBulkUpdateDialogProps) => {
    const dispatch = useAppDispatch();
    const selectedIds = useAppSelector(getTransportAssignmentBulkSelection);
    const [currentStep, setCurrentStep] = useState(TransportOrderBulkUpdateDialogStep.VALIDATION);
    const isValidationStep = currentStep === TransportOrderBulkUpdateDialogStep.VALIDATION;
    const isSuccess = previewResult.success;
    const validOrdersCount = isSuccess ? previewResult.assignments.length : 0;
    const invalidOrders: BulkStatusError<TransportAssignmentBulkUpdatePreviewErrorCode>[] = isSuccess
        ? previewResult.invalidAssignments.map((error) => ({
              errorCode: error.errorCode,
              identifiers: error.assignments,
              identifierType: 'ORDER',
          }))
        : [];
    const nextButtonDisabled = !isSuccess || previewResult.assignments.length === 0;
    const closeButtonLabel = isValidationStep
        ? 'outboundOrderBook.common.button.cancel'
        : 'outboundOrderBook.common.button.done';

    const bulkUpdateError =
        !isSuccess && previewResult.errorCode === 'size-exceeded' ? (
            <BulkUpdateMaxSizeErrorMessage
                identifierType="ORDER"
                maxBulkSize={MAX_BULK_SELECTION_SIZE}
                totalCount={selectedIds.length}
            />
        ) : undefined;

    const duplicateVinError =
        !isSuccess && previewResult.errorCode === 'duplicate-vin' ? (
            <div>
                <FormattedMessage id="outboundOrderBook.transportAssignments.bulkUpdate.validationFeedback.failure.duplicateVin" />
            </div>
        ) : undefined;

    const handleOnClose = () => {
        if (!isValidationStep) {
            dispatch(transportAssignmentsSlice.actions.clearBulkSelection());
        }
        onClose();
    };

    return (
        <Dialog show onClose={onClose} showCloseButton bsSize={Dialog.SIZE_LG}>
            <Dialog.Title
                title={<FormattedMessage id="outboundOrderBook.transportAssignments.bulkUpdate.dialogTitle" />}
            />
            <Dialog.Body>
                {isValidationStep ? (
                    <BulkUpdateValidationFeedback
                        validItemsCount={validOrdersCount}
                        invalidItems={invalidOrders}
                        requestError={bulkUpdateError || duplicateVinError}
                    />
                ) : (
                    previewResult.success && (
                        <TransportOrderBulkUpdateFormStep validAssignments={previewResult.assignments} />
                    )
                )}
            </Dialog.Body>
            <Dialog.Footer>
                <ButtonToolbar>
                    <Button iconName={isValidationStep ? undefined : 'rioglyph rioglyph-ok'} onClick={handleOnClose}>
                        <FormattedMessage id={closeButtonLabel} />
                    </Button>
                    {isValidationStep && (
                        <Button
                            bsStyle={Button.PRIMARY}
                            onClick={() => setCurrentStep(TransportOrderBulkUpdateDialogStep.FORM)}
                            disabled={nextButtonDisabled}
                        >
                            <FormattedMessage id="outboundOrderBook.common.button.next" />
                        </Button>
                    )}
                </ButtonToolbar>
            </Dialog.Footer>
        </Dialog>
    );
};
